import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import { useNavigate } from "react-router-dom";
import { FiPlus, FiTrash2, FiLayers, FiList, FiInbox, FiFolder, FiChevronRight } from "react-icons/fi";
import {
  StandardInfoAtom,
  ListStandardInfoAtom,
  DetailStandardInfoAtom,
} from "../../../atoms/standardModelAtom";
import MobileSubNavigator from '../../../components/MobileSubNavigator';
import { getHomeLinks } from '../../../utils/homeLinks';
import {
  tokens,
  Button,
  IconBtn,
  SearchInput,
  Badge,
  Tag,
  EmptyState,
} from "../../../components/admin/AdminUI";

const Container = styled.div`
  width: 100%;
  max-width: 100vw;
  margin: 0;
  padding: 16px;
  background: ${tokens.color.appBg};
  color: ${tokens.color.text};
  min-height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden;
  font-family: ${tokens.font};
  -webkit-font-smoothing: antialiased;
`;

const Section = styled.section`
  background: ${tokens.color.surface};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.card};
  padding: 16px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px 0;
  font-size: 15px;
  font-weight: 600;
  color: ${tokens.color.heading};
  letter-spacing: -0.01em;

  & svg { width: 16px; height: 16px; color: ${tokens.color.muted}; }
`;

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
`;

const GroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 16px;
  padding: 2px;
`;

const GroupCard = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  text-align: left;
  font-family: inherit;
  font-size: 14px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  color: ${(p) => (p.$active ? tokens.color.primary : tokens.color.text)};
  background: ${(p) => (p.$active ? tokens.color.primarySoft : tokens.color.surface)};
  border: 1px solid ${(p) => (p.$active ? tokens.color.primary : tokens.color.border)};
  border-radius: ${tokens.radius.md};
  padding: 12px 14px;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;

  & .chev { color: ${(p) => (p.$active ? tokens.color.primary : tokens.color.subtle)}; flex: 0 0 auto; }
`;

const AddSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${tokens.color.surfaceMuted};
  border: 1px solid ${tokens.color.borderSubtle};
  border-radius: ${tokens.radius.md};
  padding: 14px;
`;

const AddButtonRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
`;

const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 420px;
  overflow-y: auto;
  padding: 2px;
`;

const DetailItem = styled.div`
  background: ${tokens.color.surface};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.md};
  padding: 12px 14px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 4px 0;

  & + & { border-top: 1px solid ${tokens.color.borderSubtle}; }
`;

const DetailLabel = styled.span`
  flex: 0 0 64px;
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.color.muted};
`;

const DetailValue = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: ${tokens.color.heading};
  word-break: break-word;
`;

const MobileStandardInfo = () => {
  const navigate = useNavigate();
  const [codeGroupInput, setCodeGroupInput] = useRecoilState(StandardInfoAtom);
  const [selectedCodeGroup, setSelectedCodeGroup] = useRecoilState(ListStandardInfoAtom);
  const [filterValue1, setFilterValue1] = useState("");
  const [codeGroupList, setCodeGroupList] = useState([]);
  const [selectedCodeGroupDetails, setSelectedCodeGroupDetails] = useRecoilState(DetailStandardInfoAtom);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tableKey, setTableKey] = useState(0);

  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    setCodeGroupInput("");
    setSelectedCodeGroup("");
    setFilterValue1("");
    setCodeGroupList([]);
    setSelectedCodeGroupDetails([]);
    setIsEditMode(false);
    await getItemSample();
  };

  const getItemSample = async () => {
    try {
      const res = await fetch("/request/getCommonCodeGroup", {
        credentials: "include",
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setCodeGroupList(data);
      } else {
        console.error("API response is not an array:", data);
      }
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const getStdDtl = async (codeGroup) => {
    try {
      const res = await fetch(
        `/request/getStdDtl?std_group_code=${codeGroup}`,
        { credentials: "include" }
      );
      const data = await res.json();
      setSelectedCodeGroupDetails(data);
      setTableKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const insertStdInfo = async (codeGroupInput) => {
    if (codeGroupInput === "") {
      alert("새 코드 그룹명을 입력 해 주세요.");
      return;
    }
    try {
      const res = await fetch("/request/InsertStdInfo", {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codeGroupInput }),
      });
      await res.json();
      alert(`새 코드그룹 [${codeGroupInput}] 생성`);
      initializeComponent();
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const deleteStdInfo = async (codeGroup) => {
    if (!window.confirm(`${codeGroup}을 정말 삭제하시겠습니까?`)) return;
    try {
      const res = await fetch("/request/deleteStdInfo", {
        method: "POST",
        mode: "cors",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ group_code: codeGroup }),
      });
      if (!res.ok)
        throw new Error(`Server responded with status code ${res.status}`);
      alert("삭제 되었습니다.");
      initializeComponent();
      setSelectedCodeGroupDetails([]);
    } catch (error) {
      console.log("Error: " + error);
    }
  };

  const handleCodeGroupClick = (codeGroup) => {
    setIsEditMode(false);
    getStdDtl(codeGroup);
    setSelectedCodeGroup(codeGroup);
  };

  const getFilteredRows2 = () => {
    const filterText = filterValue1.toLowerCase();
    return codeGroupList.filter((row) =>
      row.std_group_code.toLowerCase().includes(filterText)
    );
  };

  const links = getHomeLinks();
  const filteredGroups = getFilteredRows2();

  return (
    <MobileSubNavigator
      links={links}
      sectionTitle="표준정보 관리"
      backPath="/"
      showBackButton={true}
    >
      <Container>
        <Section>
          <SectionTitle>
            <FiLayers /> 코드그룹 리스트
            <Badge $tone="neutral" style={{ marginLeft: "auto" }}>{filteredGroups.length}</Badge>
          </SectionTitle>

          <SearchRow>
            <SearchInput
              placeholder="찾을 코드를 입력해주세요."
              value={filterValue1}
              onChange={(e) => setFilterValue1(e.target.value)}
              onSearch={getFilteredRows2}
              grow="1 1 auto"
            />
            <Button $variant="primary" onClick={getFilteredRows2}>
              검색
            </Button>
          </SearchRow>

          {filteredGroups.length === 0 ? (
            <EmptyState
              icon={<FiFolder />}
              title="코드그룹이 없습니다"
              description="아래에서 새 코드그룹을 추가해 보세요."
              pad="32px 16px"
            />
          ) : (
            <GroupList>
              {filteredGroups.map((row, index) => (
                <GroupCard
                  key={index}
                  $active={selectedCodeGroup === row.std_group_code}
                  onClick={() => handleCodeGroupClick(row.std_group_code)}
                >
                  <span>{row.std_group_code}</span>
                  <FiChevronRight className="chev" size={16} />
                </GroupCard>
              ))}
            </GroupList>
          )}

          <AddSection>
            <SearchInput
              value={codeGroupInput}
              onChange={(e) => setCodeGroupInput(e.target.value)}
              onSearch={() => insertStdInfo(codeGroupInput)}
              placeholder="새 코드그룹명을 입력하세요."
              grow="1 1 auto"
            />
            <AddButtonRow>
              <IconBtn
                $size="sm"
                title="선택한 코드그룹 삭제"
                onClick={() => deleteStdInfo(selectedCodeGroup)}
              >
                <FiTrash2 />
              </IconBtn>
              <Button
                $variant="primary"
                onClick={() => insertStdInfo(codeGroupInput)}
              >
                <FiPlus /> 추가
              </Button>
            </AddButtonRow>
          </AddSection>
        </Section>

        {selectedCodeGroup && (
          <Section>
            <SectionTitle>
              <FiList /> 상세 내용
              <Badge $tone="brand" $dot style={{ marginLeft: "auto" }}>{selectedCodeGroup}</Badge>
            </SectionTitle>

            <DetailList key={tableKey}>
              {selectedCodeGroupDetails && selectedCodeGroupDetails.length > 0 ? (
                selectedCodeGroupDetails.map((row, index) => (
                  <DetailItem key={index}>
                    <DetailRow>
                      <DetailLabel>그룹코드</DetailLabel>
                      <DetailValue>
                        <Tag>{row.std_group_code}</Tag>
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>명칭</DetailLabel>
                      <DetailValue>{row.std_key}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>값</DetailLabel>
                      <DetailValue>{row.std_value}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>설명</DetailLabel>
                      <DetailValue>{row.std_description}</DetailValue>
                    </DetailRow>
                  </DetailItem>
                ))
              ) : (
                <EmptyState
                  icon={<FiInbox />}
                  title="코드그룹을 선택해주세요."
                  pad="40px 16px"
                />
              )}
            </DetailList>

            {selectedCodeGroupDetails.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <Button
                  $variant="primary"
                  onClick={() => navigate('/StandardInfo_Dtail')}
                  style={{ width: "100%" }}
                >
                  상세 편집
                </Button>
              </div>
            )}
          </Section>
        )}
      </Container>
    </MobileSubNavigator>
  );
};

export default MobileStandardInfo;
